import * as React from 'react';
import classnames from 'classnames';
import { get, isObject } from 'lodash';

import { Form, FormField } from 'components/form';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import InlineEditingFieldsContext from '../../context/InlineEditingFieldsContext';

import './inline-editing-wrapper.scss';

type Props = {
  saveOnBlur?: boolean,
  inGlobalEditMode?: boolean,
  inForceEditMode?: boolean,
  onSubmit?: (a: any) => void,
  onCancel?: () => void,
  onChange?: (a: any) => void,
  onEnterEditMode?: () => void,
  renderEditMode: () => React.ReactElement<any>,
  renderViewMode: () => React.ReactElement<any>,
  validatorResolvers?: Array<any>,
  initialValue: any,
  registerInlineEditingField?: (component: React.Component) => void,
  unregisterInlineEditingField?: (component: React.Component) => void
};

type State = {
  inEditMode: boolean,
  value: any
};

class InlineEditingWrapper extends React.Component<Props, State> {
  static defaultProps = {
    validatorResolvers: [],
    saveOnBlur: false
  };

  state = {
    inEditMode: false,
    value: this.props.initialValue
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.initialValue != this.props.initialValue) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState((prevState) => ({
        ...prevState,
        value: this.props.initialValue
      }));
    }
  }

  isMultiDate(): boolean {
    const { value } = this.state;

    return Boolean(value) && isObject(value) && ('startDate' in value) && ('endDate' in value);
  }

  enterEditMode = (): void => {
    const { registerInlineEditingField, onEnterEditMode } = this.props;

    if (registerInlineEditingField) {
      registerInlineEditingField(this);
    }

    if (onEnterEditMode) {
      onEnterEditMode();
    }

    this.setState({
      inEditMode: true,
      value: this.props.initialValue
    });
  };

  exitEditMode = (): void => {
    const { unregisterInlineEditingField } = this.props;

    if (unregisterInlineEditingField) {
      unregisterInlineEditingField(this);
    }

    this.setState({
      inEditMode: false
    });
  };

  handleExitMode = (): void => {
    const { onCancel } = this.props;

    this.exitEditMode();

    if (onCancel) {
      onCancel();
    }
  };

  handleValueChange = (e: any, endDate?: string): void => {
    const { onChange } = this.props;

    const value = this.isMultiDate() ?
      { startDate: e, endDate } :
      get(e, 'currentTarget.value', e);

    if (onChange) {
      onChange(value);
    }

    this.setState({
      value
    });
  };

  handleSubmit = (): void => {
    const { onSubmit } = this.props;
    const { value } = this.state;

    if (onSubmit) {
      onSubmit(value);
    }

    this.exitEditMode();
  };

  onValidationErrors = (): void => {
    // validation failed, keep edit mode activated
  };

  render(): React.ReactNode {
    const {
      renderViewMode,
      renderEditMode,
      validatorResolvers,
      inGlobalEditMode,
      inForceEditMode,
      saveOnBlur
    } = this.props;
    const { inEditMode, value } = this.state;

    const isMultiDate = this.isMultiDate();

    const wrapperViewClasses = classnames({
      'inline-editing-wrapper': true,
      'inline-editing-wrapper--view': true,
      'inline-editing-wrapper--disabled': inGlobalEditMode,
      'inline-editing-wrapper--short': isMultiDate
    });

    const wrapperEditClasses = classnames({
      'inline-editing-wrapper': true,
      'inline-editing-wrapper--edit': true,
      'inline-editing-wrapper--short': isMultiDate
    });

    let extraEditProps = {};
    if (saveOnBlur) {
      extraEditProps = {
        onBlur: this.handleSubmit
      };
    }

    return (inForceEditMode || inEditMode) ? (
      <div className={wrapperEditClasses}>
        <Form
          className="inline-editing-wrapper__form"
          onSubmit={this.handleSubmit}
          onValidationErrors={this.onValidationErrors}
        >
          <FormField
            validators={(validatorResolvers || []).map(validatorResolver => validatorResolver(value))}
            direction="column"
            showValidationIcon={false}
          >
            {
              isMultiDate ? (
                React.cloneElement(renderEditMode(), {
                  onChange: this.handleValueChange,
                  startDate: value.startDate,
                  endDate: value.endDate,
                  ...extraEditProps
                })
              ) : (
                React.cloneElement(renderEditMode(), {
                  onChange: this.handleValueChange,
                  value,
                  ...extraEditProps
                })
              )
            }
          </FormField>
          {!saveOnBlur && (
            <div className="inline-editing-wrapper__actions">
              <button
                className="inline-editing-wrapper__action"
                type="button"
                onClick={this.handleExitMode}
              >
                <SVGDynamicIcon
                  name="cross-custom-fg"
                  width={8}
                  height={8}
                  customColor="red"
                />
              </button>
              <button
                className="inline-editing-wrapper__action"
                type="submit"
              >
                <SVGDynamicIcon
                  name="checkmark-custom-fg"
                  width={8}
                  height={8}
                  customColor="primary"
                />
              </button>
            </div>
          )}
        </Form>
      </div>
    ) : (
      <div
        className={wrapperViewClasses}
        onClick={!inGlobalEditMode ? this.enterEditMode : undefined}
      >
        {renderViewMode()}
      </div>
    );
  }
}

export default (props: Props) => (
  <InlineEditingFieldsContext.Consumer>
    {
      ({ registerInlineEditingField, unregisterInlineEditingField }) => (
        <InlineEditingWrapper
          {...props}
          registerInlineEditingField={registerInlineEditingField}
          unregisterInlineEditingField={unregisterInlineEditingField}
        />
      )
    }
  </InlineEditingFieldsContext.Consumer>
);
